import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode, useRef } from 'react';
import { User } from '../types';
import { authApi, usersApi, setToken, clearToken, hasToken } from '../api/client';
import {
  ACTIVITY_THROTTLE_MS,
  clearLastActivity,
  hasIdleSessionExpired,
  readLastActivity,
  writeLastActivity,
  IDLE_TIMEOUT_MS,
  LAST_ACTIVITY_KEY,
} from '../utils/idleSession';

interface AuthContextType {
  user: User | null;
  login: (userId: string) => void;
  logout: () => void;
  isAuthenticated: boolean;
  addUser: (data: { username: string; displayName: string; email: string; password: string; recaptchaToken?: string }) => Promise<{ success: true; user: User } | { success: false; error: string }>;
  getAuthor: (id: string) => User | undefined;
  getUsers: () => User[];
  loginWithPassword: (identifier: string, password: string, recaptchaToken?: string) => Promise<{ ok: boolean; error?: string }>;
  loginDemo: (userId: string) => Promise<boolean>;
  setUserFromToken: () => Promise<void>;
  resendVerificationEmail: (email: string) => Promise<{ success: boolean; message: string }>;
  loadUsers: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [usersCache, setUsersCache] = useState<User[]>([]);
  const idleTimerRef = useRef<number | null>(null);
  const lastActivityRef = useRef<number>(0);

  const clearAuthState = useCallback(() => {
    clearToken();
    clearLastActivity();
    setUser(null);
  }, []);

  const setUserFromToken = useCallback(async () => {
    if (!hasToken()) {
      setUser(null);
      return;
    }

    const lastActivity = readLastActivity();
    if (hasIdleSessionExpired(lastActivity)) {
      clearAuthState();
      return;
    }

    const baselineActivity = lastActivity ?? writeLastActivity();
    lastActivityRef.current = baselineActivity;

    try {
      const me = await authApi.me();
      setUser({
        id: me.id,
        username: me.username,
        displayName: me.displayName,
        avatarUrl: me.avatarUrl,
        bio: me.bio,
        location: me.location,
      });
      lastActivityRef.current = writeLastActivity();
    } catch {
      clearAuthState();
    }
  }, [clearAuthState]);

  useEffect(() => {
    setUserFromToken();
  }, [setUserFromToken]);

  const refreshUsers = useCallback(async () => {
    try {
      const list = await usersApi.list();
      setUsersCache(list);
    } catch {
      setUsersCache([]);
    }
  }, []);

  const login = useCallback((_userId: string) => {
    void setUserFromToken();
  }, [setUserFromToken]);

  const logout = useCallback(async () => {
    try {
      if (hasToken()) await authApi.logout();
    } catch (_) {}
    clearAuthState();
  }, [clearAuthState]);

  const addUser = useCallback(
    async (data: { username: string; displayName: string; email: string; password: string; recaptchaToken?: string }) => {
      try {
        const res = await authApi.register(
          data.username,
          data.displayName,
          data.email,
          data.password,
          data.recaptchaToken
        );
        // Do not log the user in automatically; they must verify their email first.
        await refreshUsers();
        return {
          success: true as const,
          user: { id: '', username: data.username, displayName: data.displayName },
          message: res.message ?? 'Account created. Check your email to verify before signing in.',
        };
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Registration failed.';
        const isNetworkError =
          message === 'Failed to fetch' ||
          (e instanceof TypeError && e.message?.includes('fetch'));
        const friendlyMessage = isNetworkError
          ? "Can't connect to the server. Make sure the RideSpot API is running (see ridespot-api/README.md)."
          : message;
        return { success: false as const, error: friendlyMessage };
      }
    },
    [refreshUsers]
  );

  const loginWithPassword = useCallback(
    async (identifier: string, password: string, recaptchaToken?: string) => {
      try {
        const res = await authApi.login(identifier, password, recaptchaToken);
        setToken(res.token);
        lastActivityRef.current = writeLastActivity();
        await setUserFromToken();
        return { ok: true };
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Invalid username/email or password.';
        return { ok: false, error: message };
      }
    },
    [setUserFromToken]
  );

  const loginDemo = useCallback(async (userId: string) => {
    try {
      const res = await authApi.demoLogin(userId);
      setToken(res.token);
      lastActivityRef.current = writeLastActivity();
      await setUserFromToken();
      return true;
    } catch {
      return false;
    }
  }, [setUserFromToken]);

  useEffect(() => {
    if (!user || !hasToken()) {
      if (idleTimerRef.current != null) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      return;
    }

    const scheduleLogout = (lastActivity: number) => {
      lastActivityRef.current = lastActivity;
      if (idleTimerRef.current != null) window.clearTimeout(idleTimerRef.current);
      const remaining = IDLE_TIMEOUT_MS - (Date.now() - lastActivity);
      if (remaining <= 0) {
        void logout();
        return;
      }
      idleTimerRef.current = window.setTimeout(() => {
        void logout();
      }, remaining);
    };

    const syncOrLogout = (touch = false) => {
      const stored = readLastActivity();
      if (hasIdleSessionExpired(stored)) {
        void logout();
        return;
      }
      const nextActivity = touch ? writeLastActivity() : stored ?? writeLastActivity();
      scheduleLogout(nextActivity);
    };

    const recordActivity = () => {
      const now = Date.now();
      if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) return;
      scheduleLogout(writeLastActivity(now));
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') syncOrLogout(true);
    };

    const handleStorage = (event: StorageEvent) => {
      if (!hasToken()) {
        clearAuthState();
        return;
      }
      if (event.key === null || event.key === LAST_ACTIVITY_KEY) syncOrLogout(false);
    };

    syncOrLogout(false);

    const activityEvents: Array<keyof WindowEventMap> = ['mousemove', 'mousedown', 'keydown', 'scroll', 'focus'];
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, recordActivity, { passive: true });
    });
    window.addEventListener('touchstart', recordActivity, { passive: true });
    window.addEventListener('storage', handleStorage);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (idleTimerRef.current != null) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, recordActivity);
      });
      window.removeEventListener('touchstart', recordActivity);
      window.removeEventListener('storage', handleStorage);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, logout, clearAuthState]);

  const getAuthor = useCallback(
    (id: string) => usersCache.find((u) => u.id === id),
    [usersCache]
  );

  const getUsers = useCallback(() => usersCache, [usersCache]);

  const resendVerificationEmail = useCallback(async (email: string) => {
    try {
      const res = await authApi.resendVerificationEmail(email);
      return {
        success: true,
        message:
          res.message ??
          'If an account exists for that email, a new verification email has been sent.',
      };
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Could not resend verification email.';
      return { success: false, message };
    }
  }, []);

  const value: AuthContextType = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    addUser,
    getAuthor,
    getUsers,
    loginWithPassword,
    loginDemo,
    setUserFromToken,
    resendVerificationEmail,
    loadUsers: refreshUsers,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
